import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SpinnerProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClass: Record<NonNullable<SpinnerProps['size']>, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-5 w-5',
  lg: 'h-8 w-8',
};

/** Small rotating icon. Use inline (button, badge, table cell). */
export function Spinner({ className, size = 'md' }: SpinnerProps) {
  return <Loader2 className={cn('animate-spin text-muted-foreground', sizeClass[size], className)} />;
}

interface LoadingStateProps {
  label?: string;
  className?: string;
}

/** Full-section centered spinner + label. Use as a placeholder for whole pages or big sections while data loads. */
export function LoadingState({ label = 'Načítám…', className }: LoadingStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 py-16 text-sm text-muted-foreground', className)}>
      <Spinner size="lg" />
      <span>{label}</span>
    </div>
  );
}

interface TableSkeletonProps {
  /** How many placeholder rows. */
  rows?: number;
  /** How many cells per row. */
  cols?: number;
}

/** Animated grey placeholder rows for a `<Table>` body — renders inside `<TableBody>`. */
export function TableSkeleton({ rows = 5, cols = 4 }: TableSkeletonProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="border-b">
          {Array.from({ length: cols }).map((_, c) => (
            <td key={c} className="p-3">
              <div className="h-4 w-full max-w-[200px] rounded bg-muted/60 animate-pulse" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
